<template>
	<div
		v-if="visibleSuggestions.length > 0"
		class="extraction-banner"
		:class="{ 'extraction-banner--warning': variant === 'warning' }"
	>
		<div class="extraction-banner-content">
			<span class="ht-icon extraction-banner-icon">{{
				variant === "warning" ? "warning" : "description"
			}}</span>
			<div class="extraction-banner-text">
				<span class="extraction-banner-title">
					{{
						hasReactivated
							? "Eerder toegepast — doelsectie verwijderd"
							: (label ??
								`Gegevens beschikbaar uit ${visibleSuggestions.length === 1 ? "document" : `${visibleSuggestions.length} documenten`}`)
					}}
				</span>
				<span
					v-for="s in visibleSuggestions"
					:key="s.id"
					class="extraction-banner-doc"
				>
					{{ s.documentName ?? "Onbekend document" }}
					<span class="extraction-banner-label">{{
						s.classificationLabel
					}}</span>
					<span
						v-if="s.reactivatedAfterDelete === true"
						class="extraction-banner-unmatch"
					>
						— eerder toegepast, doelsectie verwijderd
					</span>
					<span
						v-else-if="s.matchedCustomerId === null"
						class="extraction-banner-unmatch"
					>
						— klant nog niet bepaald
					</span>
					<button class="extraction-banner-open" @click.stop="emit('open', s)">
						{{ actionLabel ?? "Bekijken" }}
					</button>
				</span>
			</div>
			<div class="extraction-banner-actions">
				<ExtractionDismissMenu
					v-if="visibleSuggestions.length === 1"
					@dismiss="
						(reason) => emit('dismiss', visibleSuggestions[0]!.id, reason)
					"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { ExtractionSuggestion } from "@/composables/useExtractionSuggestions";
import ExtractionDismissMenu from "./ExtractionDismissMenu.vue";

const props = defineProps<{
	suggestions: ExtractionSuggestion[];
	label?: string;
	actionLabel?: string;
	variant?: "default" | "warning";
}>();

const emit = defineEmits<{
	dismiss: [suggestionId: string, reason?: string];
	open: [suggestion: ExtractionSuggestion];
}>();

const visibleSuggestions = computed(() =>
	props.suggestions.filter(
		(s) =>
			s.status !== "genegeerd" &&
			s.status !== "vervangen" &&
			s.status !== "toegepast",
	),
);

const hasReactivated = computed(() =>
	visibleSuggestions.value.some((s) => s.reactivatedAfterDelete === true),
);
</script>

<style scoped>
.extraction-banner {
	padding: 0.75rem 1rem;
	background: var(--background-secondary, #f9fafb);
	border: 1px solid var(--border, #e5e7eb);
	border-left: 3px solid var(--accent-primary, #7c3aed);
	border-radius: 0.375rem;
	margin-bottom: 1rem;
}

.extraction-banner-content {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
}

.extraction-banner-icon {
	color: var(--accent-primary, #7c3aed);
	font-size: 1.25rem;
	flex-shrink: 0;
	margin-top: 0.1rem;
}

.extraction-banner-text {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	flex: 1;
}

.extraction-banner-title {
	font-size: 0.8rem;
	font-weight: 600;
	color: var(--text-primary, #111827);
}

.extraction-banner-doc {
	font-size: 0.75rem;
	color: var(--text-muted, #6b7280);
}

.extraction-banner-label {
	font-size: 0.65rem;
	padding: 0.1rem 0.35rem;
	background: var(--accent-primary, #7c3aed);
	color: white;
	border-radius: 0.2rem;
	margin-left: 0.35rem;
}

.extraction-banner-unmatch {
	color: var(--color-warning, #f59e0b);
	font-style: italic;
}

.extraction-banner--warning {
	border-left-color: var(--color-warning, #f59e0b);
}

.extraction-banner--warning .extraction-banner-icon {
	color: var(--color-warning, #f59e0b);
}

.extraction-banner-open {
	font-size: 0.7rem;
	color: var(--accent-primary, #7c3aed);
	background: none;
	border: none;
	cursor: pointer;
	text-decoration: underline;
	margin-left: 0.35rem;
	padding: 0;
}

.extraction-banner-open:hover {
	opacity: 0.8;
}

.extraction-banner-actions {
	flex-shrink: 0;
}
</style>
